import type { Service } from "@oathera/contract";
import {
  isAlertFrame,
  isEventFrame,
  isMetricFrame,
  isSnapshotFrame,
  isSourceFrame,
  type ServerFrame,
  type SnapshotFrame,
  type StreamAlert,
  type StreamEvent,
} from "./frames.ts";

/**
 * The pure, framework-free reducer for the live view the stream feeds (design 8.1, 12.3).
 *
 * It holds the four topics the hub carries — the events ring, the metric series, the per-source
 * liveness and the current alerts — as immutable data, and folds server frames into it:
 *
 * - A SNAPSHOT frame REPLACES the view for its topic from the snapshot body ("apply a fresh snapshot
 *   before new deltas"): a snapshot is the authoritative state at its `pos`, so the client throws
 *   away whatever it had for that topic and starts again from the snapshot, then applies later
 *   deltas on top. The hub sends a snapshot on `initial`, `resume_invalid`, `resume_gap` and
 *   `client_requested`; in every case the correct behaviour is the same — replace, do not merge.
 * - A DELTA frame (event / metric / source / alert) applies on top of the current view, IN ORDER.
 *
 * Positions are monotonic across topics in the tenant's stream and delivery is at-least-once, so the
 * reducer DEDUPES by `pos`: a frame whose `pos` is not greater than the highest already applied is
 * ignored (design 8.1 "a client ignores a `pos` it has seen"). A snapshot carries the `pos` of the
 * head it was read at and resets the seen-position watermark to it, because the snapshot is the new
 * baseline and any earlier delta is already reflected in it.
 *
 * The reducer is deliberately NOT React state: the stream client (a plain object) drives it and the
 * observable store (`state/stream-view.ts`) publishes it to React with `useSyncExternalStore`.
 */

/** A point in a metric series: a time in ms and the bucket value after roll-up. */
export interface MetricPoint {
  readonly t: number;
  readonly v: number;
}

/** One metric series, keyed in {@link MetricSeriesMap} by `${name}` and distinguished by key. */
export interface MetricSeries {
  readonly name: string;
  /** The service that emitted it; the hub always sends it so same-named series are told apart. */
  readonly service: Service | null;
  readonly labels: Readonly<Record<string, string>>;
  /** Points oldest-first. Bounded to the retained window by {@link METRIC_POINTS_PER_SERIES}. */
  readonly points: readonly MetricPoint[];
}

/** The liveness of one source (design 8.1 "Liveness"). */
export interface SourceLiveness {
  readonly sourceId: string;
  readonly service: Service | null;
  readonly state: "live" | "silent";
  /** When the source entered this state, ms. */
  readonly since: number;
}

/** The current state of one alert, keyed by rule id. */
export interface AlertView {
  readonly ruleId: string;
  readonly state: StreamAlert["state"];
  /** The full alert body if a snapshot carried it; deltas carry only the state change. */
  readonly alert: StreamAlert | null;
}

/** The whole live view. Immutable; a new object is produced on every applied frame. */
export interface StreamView {
  /** Events oldest-first (arrival order), bounded to {@link EVENTS_RING_CAPACITY}. */
  readonly events: readonly StreamEvent[];
  /** Metric series keyed by a stable series key (name + sorted labels + service). */
  readonly metrics: ReadonlyMap<string, MetricSeries>;
  /** The window the metric snapshot was taken over, in seconds, when known. */
  readonly metricWindowS: number | null;
  /** Source liveness keyed by source id. */
  readonly sources: ReadonlyMap<string, SourceLiveness>;
  /** Alerts keyed by rule id. */
  readonly alerts: ReadonlyMap<string, AlertView>;
  /**
   * The highest `pos` folded in so far, or `-1` before any frame. Used to dedupe at-least-once
   * redeliveries and to know what a resume should continue after.
   */
  readonly lastPos: number;
}

/** The hub keeps the last 2,000 events (design 8); the client mirrors that bound for its ring. */
export const EVENTS_RING_CAPACITY = 2000;
/** The hub keeps ~an hour of 10 s points; cap each series so a long session cannot grow unbounded. */
export const METRIC_POINTS_PER_SERIES = 360;

export const EMPTY_VIEW: StreamView = {
  events: [],
  metrics: new Map(),
  metricWindowS: null,
  sources: new Map(),
  alerts: new Map(),
  lastPos: -1,
};

/** A stable key for a metric series: a metric name can repeat across services and label sets. */
export function metricSeriesKey(
  name: string,
  service: Service | null,
  labels: Readonly<Record<string, string>>,
): string {
  const sorted = Object.keys(labels)
    .sort()
    .map((k) => `${k}=${labels[k] ?? ""}`)
    .join(",");
  return `${service ?? ""}\u0000${name}\u0000${sorted}`;
}

/**
 * Fold one server frame into the view and return the next view. A frame already seen (its `pos` is
 * not ahead of `lastPos`) returns the SAME view reference, so an observable store can skip a
 * needless notification. A snapshot always applies: it resets the baseline.
 */
export function applyFrame(view: StreamView, frame: ServerFrame): StreamView {
  if (isSnapshotFrame(frame)) return applySnapshot(view, frame);

  // Deltas are deduped by position (at-least-once delivery).
  if (frame.pos <= view.lastPos) return view;

  if (isEventFrame(frame)) {
    return { ...view, events: pushEvent(view.events, frame.data), lastPos: frame.pos };
  }
  if (isMetricFrame(frame)) {
    return {
      ...view,
      metrics: applyMetricPoint(view.metrics, {
        name: frame.name,
        service: frame.service ?? null,
        labels: frame.labels,
        // The metric bucket is Unix seconds; the view keeps points in ms for charting.
        point: { t: frame.bucket * 1000, v: frame.v },
      }),
      lastPos: frame.pos,
    };
  }
  if (isSourceFrame(frame)) {
    const next = new Map(view.sources);
    next.set(frame.source_id, {
      sourceId: frame.source_id,
      service: frame.service ?? null,
      state: frame.state,
      since: frame.since,
    });
    return { ...view, sources: next, lastPos: frame.pos };
  }
  if (isAlertFrame(frame)) {
    const next = new Map(view.alerts);
    const existing = next.get(frame.rule_id);
    next.set(frame.rule_id, {
      ruleId: frame.rule_id,
      state: frame.state,
      // A delta changes only the state; the full body (if any) is retained from the snapshot.
      alert: existing?.alert ?? null,
    });
    return { ...view, alerts: next, lastPos: frame.pos };
  }
  return view;
}

/**
 * Replace the snapshotted topic's view from the snapshot body and reset the position watermark to
 * the snapshot's `pos`. Topics the snapshot does not carry are left as they are (the hub snapshots
 * per topic). This is the "fresh snapshot before new deltas" step.
 */
export function applySnapshot(view: StreamView, snapshot: SnapshotFrame): StreamView {
  let next: StreamView = { ...view, lastPos: snapshot.pos };

  if (snapshot.topic === "events") {
    const events = snapshot.events ?? [];
    // The hub sends the ring oldest-first; keep at most the client's ring capacity.
    next = { ...next, events: events.slice(-EVENTS_RING_CAPACITY) };
  } else if (snapshot.topic === "metrics") {
    next = { ...next, metrics: buildMetricsFromSnapshot(snapshot), metricWindowS: snapshot.window_s ?? null };
  } else if (snapshot.topic === "sources") {
    const sources = new Map<string, SourceLiveness>();
    for (const s of snapshot.sources ?? []) {
      sources.set(s.source_id, {
        sourceId: s.source_id,
        service: s.service ?? null,
        state: s.state,
        since: s.since,
      });
    }
    next = { ...next, sources };
  } else if (snapshot.topic === "alerts") {
    const alerts = new Map<string, AlertView>();
    for (const a of snapshot.alerts ?? []) {
      alerts.set(a.rule_id, { ruleId: a.rule_id, state: a.state, alert: a });
    }
    next = { ...next, alerts };
  }
  return next;
}

function pushEvent(events: readonly StreamEvent[], event: StreamEvent): readonly StreamEvent[] {
  const next = events.length >= EVENTS_RING_CAPACITY ? events.slice(events.length - EVENTS_RING_CAPACITY + 1) : events.slice();
  next.push(event);
  return next;
}

function applyMetricPoint(
  metrics: ReadonlyMap<string, MetricSeries>,
  item: {
    name: string;
    service: Service | null;
    labels: Readonly<Record<string, string>>;
    point: MetricPoint;
  },
): ReadonlyMap<string, MetricSeries> {
  const key = metricSeriesKey(item.name, item.service, item.labels);
  const next = new Map(metrics);
  const existing = next.get(key);
  const points = mergePoint(existing?.points ?? [], item.point);
  next.set(key, {
    name: item.name,
    service: item.service,
    labels: item.labels,
    points,
  });
  return next;
}

/**
 * Merge a point into a series' points. A metric frame carries the bucket value AFTER roll-up and
 * REPLACES the stored one for that bucket (design 8.1 "Snapshot contents"), so a point at an
 * existing time overwrites it rather than appending; a point at a new time appends in time order.
 * The series is bounded to the retained window.
 */
function mergePoint(points: readonly MetricPoint[], point: MetricPoint): readonly MetricPoint[] {
  const next = points.slice();
  const idx = next.findIndex((p) => p.t === point.t);
  if (idx >= 0) {
    next[idx] = point;
  } else {
    // Append; if it arrived out of order (rare), insert at the right place to keep points sorted.
    const last = next[next.length - 1];
    if (last === undefined || point.t >= last.t) {
      next.push(point);
    } else {
      const at = next.findIndex((p) => p.t > point.t);
      next.splice(at, 0, point);
    }
  }
  return next.length > METRIC_POINTS_PER_SERIES ? next.slice(next.length - METRIC_POINTS_PER_SERIES) : next;
}

function buildMetricsFromSnapshot(snapshot: SnapshotFrame): ReadonlyMap<string, MetricSeries> {
  const metrics = new Map<string, MetricSeries>();
  const series = snapshot.series ?? {};
  for (const name of Object.keys(series)) {
    const list = series[name] ?? [];
    for (const s of list) {
      const service = s.service ?? null;
      const labels = s.labels;
      const points = s.points
        .map((p): MetricPoint => ({ t: p.t, v: p.v }))
        .slice(-METRIC_POINTS_PER_SERIES);
      metrics.set(metricSeriesKey(name, service, labels), { name, service, labels, points });
    }
  }
  return metrics;
}
